/**
 * The sidebar: one primary action, four sections, sign out.
 *
 * The primary action sits ABOVE the nav list rather than inside it. Ordering a
 * video is not one of several equal destinations - it is the thing the product
 * is for, so it gets the shape Linear's "New issue" and Gmail's "Compose" have.
 * While "New Brief" was one of nine equal rows, the product was claiming that
 * ordering matters as much as reading an invoice.
 *
 * Orange fill takes DARK text (`--on-orange`): white on #FF6B35 measures
 * 2.84:1 and fails WCAG AA.
 *
 * @module web/screens/app/Sidebar
 */
import type { ReactNode } from "react";
import type { Tab } from "../../routes.ts";
import { useBrand } from "../../state/BrandContext.tsx";
import { allowedTab } from "./roleView.ts";
import { BrandSwitcher } from "./BrandSwitcher.tsx";

interface NavEntry {
  tab: Tab;
  label: string;
  icon: ReactNode;
}

const NAV: NavEntry[] = [
  { tab: "start", label: "Home", icon: <><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/></> },
  { tab: "work", label: "Work", icon: <><circle cx="6" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><path d="M6 8.5v7"/><path d="M11 6h7"/><path d="M11 18h7"/></> },
  { tab: "billing", label: "Billing", icon: <><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/><circle cx="18" cy="14" r="1"/></> },
  { tab: "settings", label: "Settings", icon: <><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></> },
];

export function Sidebar({ tab, onNavigate, onCompose, onAgentMode, onSignOut, walletLabel, walletPositive, showWallet, open, workBadge = 0 }: {
  tab: Tab;
  /**
   * Submissions waiting for the customer's Confirm (spec 2026-09-20). The one
   * coloured mark in the nav - Von Restorff - so 0 renders nothing at all.
   */
  workBadge?: number;
  onNavigate: (tab: Tab) => void;
  /** The primary action: start a new brief. */
  onCompose: () => void;
  /** Settings → Agents: connect the person's own AI agent (spec 2026-10-02-agent-connector). */
  onAgentMode: () => void;
  onSignOut: () => void;
  walletLabel: string;
  walletPositive: boolean;
  /**
   * Whether the balance pill belongs on screen at all. A brand that does not
   * run on a wallet has no balance, and a permanent grey "0,00 €" was the
   * first number every new customer read - it looks like a deficiency rather
   * than an irrelevance.
   */
  showWallet: boolean;
  /** Drawer state. Only has an effect below 769px. */
  open: boolean;
}) {
  const { brand } = useBrand();
  const role = brand?.role ?? "owner";
  return (
    <aside className={`sidebar${open ? " open" : ""}`} id="app-sidebar">
      <div className="sb-brand">
        <img className="sb-logo" src="/aditor-logo.png" alt="Aditor logo" />
        <div className="sb-name">Aditor</div>
      </div>

      <BrandSwitcher />

      <button className="sb-compose" data-screen="compose" onClick={onCompose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        New brief
      </button>

      <nav className="sb-nav">
        {NAV.filter(entry => allowedTab(role, entry.tab, brand?.billingOwner ?? role === "owner")).map(entry => (
          <button
            key={entry.tab}
            className={`nav-item${entry.tab === tab ? " active" : ""}`}
            data-screen={entry.tab}
            onClick={() => onNavigate(entry.tab)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {entry.icon}
            </svg>
            {entry.label}
            {entry.tab === "work" && workBadge > 0 && (
              <span className="nav-badge" id="work-badge" aria-label={`${workBadge} to confirm`}>{workBadge}</span>
            )}
            {entry.tab === "billing" && showWallet && (
              <span className={`pill wallet-pill ${walletPositive ? "pill-live" : "pill-neutral"}`}>{walletLabel}</span>
            )}
          </button>
        ))}
      </nav>

      {allowedTab(role, "settings", brand?.billingOwner ?? role === "owner") && (
        <button className="nav-item sb-agent" data-screen="agents" onClick={onAgentMode}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="7" width="16" height="12" rx="4" />
            <path d="M12 7V4" />
            <circle cx="9.5" cy="13" r="1" fill="currentColor" stroke="none" />
            <circle cx="14.5" cy="13" r="1" fill="currentColor" stroke="none" />
            <path d="M2 12v2M22 12v2" />
          </svg>
          Agent mode
        </button>
      )}

      <div className="sb-footer">
        <button className="nav-item" onClick={onSignOut}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Sign Out
        </button>
      </div>
    </aside>
  );
}
